function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import BottomBar from '../../../components/BottomBar';
import { titleFontSizesBOH, isDefined } from '../../../helpers/utils';
import { formatDuration, parseWithoutZeros } from '../../helpers/boh_spotlights';
import EmojiGood from './EmojiGood.svg';
import EmojiBad from './EmojiBad.svg';
import huddleBoardEntries from './huddleBoardEntries.json';
import './HuddleBoard.scss';
import * as bohLib from '@shift/boh';
const {
  BOH_DEFAULT_GOALS_VALUES: defaultValues
} = bohLib;
const statusHelper = {
  good: {
    image: EmojiGood,
    word: 'Awesome!',
    text: 'We reached the goal.'
  },
  bad: {
    image: EmojiBad,
    word: 'Bummer!',
    text: 'We didn\'t reach the goal.'
  }
};
function HuddleBoardItem({
  fullScreen,
  last_updated,
  goal,
  value,
  format,
  timezone,
  customText,
  customDisplay,
  overall,
  styles = {},
  should_be_higher = true,
  id
}) {
  if (value && !goal) {
    goal = defaultValues[`boh.goals.${id}`];
  }
  if (customDisplay && customDisplay.show_as_percentage) {
    format = 'percentage';
    if (isDefined(value)) {
      value = value * 100;
    }
    if (isDefined(goal)) {
      goal = goal * 100;
    }
  }
  const invalidStatus = !isDefined(value) || !isDefined(goal);
  const difference = should_be_higher ? parseFloat(value) - parseFloat(goal) : parseFloat(goal) - parseFloat(value);
  const isGoalBeated = Math.sign(difference) > -1;
  const diffPercentage = Math.abs(difference) / parseFloat(goal) * 100;
  const statusKey = React.useMemo(() => {
    if (invalidStatus) {
      return 'invalid';
    }
    return isGoalBeated ? 'good' : 'bad';
  }, [invalidStatus, isGoalBeated]);
  const lastUpdated = React.useMemo(() => {
    var _DateTime$fromISO;
    return (_DateTime$fromISO = DateTime.fromISO(last_updated, {
      zone: timezone
    })) === null || _DateTime$fromISO === void 0 ? void 0 : _DateTime$fromISO.setLocale('en-US');
  }, [last_updated, timezone]);
  const formattedLastUpdated = React.useMemo(() => `As of ${lastUpdated.isValid ? lastUpdated === null || lastUpdated === void 0 ? void 0 : lastUpdated.toFormat('MM/dd') : 'N/A'} at ${lastUpdated.isValid ? lastUpdated === null || lastUpdated === void 0 ? void 0 : lastUpdated.toFormat('t') : 'N/A'}`, [lastUpdated]);
  const formattedGoal = React.useMemo(() => {
    if (!isDefined(goal) || !isDefined(value)) return '-';
    let goalString = goal.toString();
    if (format === 'percentage') goalString += '%';
    if (format === 'time') goalString = formatDuration(goalString);
    return goalString;
  }, [goal, format, value]);
  const formatValue = React.useCallback(v => {
    switch (format) {
      case 'percentage':
        return `${parseWithoutZeros(v)}%`;
      case 'time':
        return (v < 0 ? '-' : '') + formatDuration(v);
      case 'number':
        return v.toFixed(2);
      default:
        return v;
    }
  }, [format]);
  const formatDiffPercentage = React.useCallback(() => {
    if (invalidStatus || !isDefined(diffPercentage)) {
      return '-';
    }
    if (goal <= 0) {
      return '';
    }
    return `${diffPercentage.toFixed(1)} %`;
  }, [diffPercentage, goal, invalidStatus]);
  const defaultStyle = {
    color: styles.color.primary,
    fontFamily: styles.fontFamily.primary
  };
  if (fullScreen) {
    return /*#__PURE__*/React.createElement("div", {
      className: `huddleboard-item status-${statusKey} fullScreen`,
      style: defaultStyle
    }, /*#__PURE__*/React.createElement("div", {
      className: "primary-section"
    }, statusHelper[statusKey] && /*#__PURE__*/React.createElement("div", {
      className: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "status__word"
    }, statusHelper[statusKey].word), /*#__PURE__*/React.createElement("span", {
      className: "status__text"
    }, statusHelper[statusKey].text), /*#__PURE__*/React.createElement("img", {
      className: "status__emoji",
      src: statusHelper[statusKey].image,
      alt: "emoji"
    })), /*#__PURE__*/React.createElement("div", {
      className: "kpi"
    }, /*#__PURE__*/React.createElement("div", {
      className: "kpi__value",
      style: {
        fontFamily: styles.fontFamily.secondary
      }
    }, isDefined(value) ? formatValue(value) : '-'), /*#__PURE__*/React.createElement("div", {
      className: "kpi__description",
      style: {
        color: styles.color.secondary
      }
    }, customText === null || customText === void 0 ? void 0 : customText.label), /*#__PURE__*/React.createElement("div", {
      className: "kpi__date"
    }, formattedLastUpdated))), /*#__PURE__*/React.createElement("div", {
      className: "secondary-section"
    }, /*#__PURE__*/React.createElement("div", {
      className: "percentage"
    }, /*#__PURE__*/React.createElement("div", {
      className: "percentage__value",
      style: {
        fontFamily: styles.fontFamily.secondary
      }
    }, formatDiffPercentage(diffPercentage)), /*#__PURE__*/React.createElement("div", {
      className: "percentage__description"
    }, overall[statusKey])), /*#__PURE__*/React.createElement("div", {
      className: "goal",
      style: {
        fontFamily: styles.fontFamily.secondary,
        backgroundColor: styles.accent.backgroundColor,
        color: styles.accent.color
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "goal__value"
    }, /*#__PURE__*/React.createElement("span", null, "GOAL: "), formattedGoal), (customText === null || customText === void 0 ? void 0 : customText.goal_description) && /*#__PURE__*/React.createElement("div", {
      className: "goal__description"
    }, customText === null || customText === void 0 ? void 0 : customText.goal_description))));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: `huddleboard-item status-${statusKey} multiple`,
    style: defaultStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-header__date"
  }, formattedLastUpdated), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-header__custom-text",
    style: {
      color: styles.color.secondary
    }
  }, customText === null || customText === void 0 ? void 0 : customText.label)), /*#__PURE__*/React.createElement("hr", {
    className: "huddleboard-divider",
    style: {
      backgroundColor: styles.color.primary
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-body"
  }, statusHelper[statusKey] && /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-feedback"
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-feedback__word"
  }, statusHelper[statusKey].word), /*#__PURE__*/React.createElement("span", null, ' '), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-feedback__text"
  }, statusHelper[statusKey].text), /*#__PURE__*/React.createElement("img", {
    className: "huddleboard-status__emoji",
    src: statusHelper[statusKey].image,
    alt: "emoji"
  })), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-kpi"
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-value",
    style: {
      fontFamily: styles.fontFamily.secondary
    }
  }, isDefined(value) ? formatValue(value) : '-'), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-goal"
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-goal__value"
  }, "GOAL ", formattedGoal), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-goal__description"
  }, customText === null || customText === void 0 ? void 0 : customText.goal_description))), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-overall"
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-overall__value",
    style: {
      fontFamily: styles.fontFamily.secondary
    }
  }, formatDiffPercentage(diffPercentage)), /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-overall__description"
  }, overall[statusKey]))));
}
const HuddleBoard = ({
  metadata,
  data,
  text,
  bohStyle
}) => {
  const {
    logo,
    custom_text = {},
    custom_display = {},
    boh_huddleboard_entries = []
  } = metadata;
  const {
    title_color,
    body_color,
    accent_color,
    text_accent_color,
    title_font_family,
    body_font_family
  } = bohStyle;
  const itemStyles = {
    color: {
      primary: body_color,
      secondary: title_color
    },
    fontFamily: {
      primary: body_font_family,
      secondary: title_font_family
    },
    accent: {
      color: text_accent_color,
      backgroundColor: accent_color
    }
  };
  const timezone = data === null || data === void 0 ? void 0 : data.timezone;
  const board_entries = useMemo(() => {
    return boh_huddleboard_entries.map(entry_key => huddleBoardEntries.find(e => e.id === entry_key)).filter(Boolean);
  }, [boh_huddleboard_entries]);
  const fullScreen = board_entries.length === 1;
  const hide_poweredbyshift = metadata.hide_poweredbyshift;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BottomBar, {
    isTop: true,
    isFat: true,
    title: text.title,
    logo: logo,
    accent_color: accent_color,
    accent_text_color: text.accent_color,
    title_font_family: text.title_font_family,
    title_font_size: titleFontSizesBOH.medium + 'px',
    hide_poweredbyshift: hide_poweredbyshift
  }), /*#__PURE__*/React.createElement("div", {
    className: `huddleboard-container ${fullScreen ? 'fullScreen' : 'multiple'}`
  }, board_entries.map(entry => /*#__PURE__*/React.createElement("div", {
    key: entry.id,
    className: "huddleboard-item-container",
    style: {
      fontFamily: body_font_family
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "huddleboard-item-title",
    style: {
      color: title_color
    }
  }, entry.title), /*#__PURE__*/React.createElement(HuddleBoardItem, _extends({
    fullScreen: fullScreen,
    styles: itemStyles,
    timezone: timezone,
    customText: custom_text === null || custom_text === void 0 ? void 0 : custom_text[entry.id],
    customDisplay: custom_display === null || custom_display === void 0 ? void 0 : custom_display[entry.id]
  }, entry, data === null || data === void 0 ? void 0 : data.data[entry.id]))))));
};
export default HuddleBoard;